import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import GoogleFontLoader from './GoogleFontLoader';
import './Dashboard.css';
import html2canvas from 'html2canvas';

const CardPreview = ({ cardDesign, businessInfo }) => {
  const {
    layout,
    customBackground,
    nameColor,
    namePosition,
    logoPosition,
    qrPosition,
    stampAreaPosition,
    stampColor,
    font,
    coffeeCount
  } = cardDesign;

  let backgroundStyle = {};
  if (customBackground) {
    backgroundStyle = { backgroundImage: `url(${customBackground})` };
  } else if (layout && layout.image) {
    const imageUrl = `${process.env.PUBLIC_URL}${layout.image}`;
    backgroundStyle = { backgroundImage: `url(${imageUrl})` };
  }

  const cardId = `${businessInfo.name.slice(0, 3).toUpperCase()}${Date.now().toString().slice(-6)}`;

  return (
    <div className="card-preview" style={backgroundStyle}>
      <GoogleFontLoader font={font} />
      {businessInfo.logo && (
        <img 
          src={businessInfo.logo}
          alt="Logo" 
          className="business-logo"
          style={{ 
            position: 'absolute',
            left: `${logoPosition.x}px`,
            top: `${logoPosition.y}px`,
            maxWidth: '60px',
            maxHeight: '60px'
          }}
        />
      )}
      <h3 
        style={{ 
          color: nameColor,
          fontFamily: `'${font}', sans-serif`,
          position: 'absolute',
          left: `${namePosition.x}px`,
          top: `${namePosition.y}px`
        }}
      >
        {businessInfo.name}
      </h3>
      <div 
        className="stamp-area" 
        style={{ 
          backgroundColor: stampColor,
          position: 'absolute',
          left: `${stampAreaPosition.x}px`,
          top: `${stampAreaPosition.y}px`,
          width: '90%',
          gridTemplateColumns: 'repeat(11, 1fr)'
        }}
      >
        {[...Array(coffeeCount || 11)].map((_, index) => (
          <div key={index} className="stamp"></div>
        ))}
      </div>
      <div 
        className="qr-code" 
        style={{ 
          position: 'absolute',
          left: `${qrPosition.x}px`,
          top: `${qrPosition.y}px`
        }}
      >
        <QRCodeSVG value={cardId} size={64} />
      </div>
    </div>
  );
};

const Dashboard = () => {
  const [cardDesign, setCardDesign] = useState(null);
  const [businessInfo, setBusinessInfo] = useState(null);
  const [clientEmail, setClientEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const navigate = useNavigate();
  const previewContainerRef = useRef(null);

  useEffect(() => {
    const storedCardDesign = JSON.parse(localStorage.getItem('cardDesign'));
    const storedBusinessInfo = JSON.parse(localStorage.getItem('businessInfo'));
    
    if (!storedCardDesign || !storedBusinessInfo) {
      navigate('/setup/business-info');
      return;
    }
    
    setCardDesign(storedCardDesign);
    setBusinessInfo(storedBusinessInfo);
  }, [navigate]);

  const handleEmailChange = (e) => {
    setClientEmail(e.target.value);
  };

  const handleSendCard = async () => {
    setIsLoading(true);
    setMessage('');

    try {
      // Salva la card su DB (posizioni + layout)
      try {
        const token = localStorage.getItem('authToken');
        const payload = {
          clientEmail,
          coffeeCount: cardDesign?.coffeeCount || 11,
          positions: {
            namePosition: cardDesign?.namePosition,
            logoPosition: cardDesign?.logoPosition,
            qrPosition: cardDesign?.qrPosition,
            stampAreaPosition: cardDesign?.stampAreaPosition
          },
          layoutConfig: {
            layout: cardDesign?.layout,
            customBackground: cardDesign?.customBackground,
            nameColor: cardDesign?.nameColor,
            stampColor: cardDesign?.stampColor,
            font: cardDesign?.font
          }
        };
        await fetch('/api/card/generate', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token ? `Bearer ${token}` : undefined
          },
          body: JSON.stringify(payload)
        });
      } catch (_) {}

      // Genera immagine della card (PNG) dalla preview finale
      let cardImage = null;
      const node = previewContainerRef.current?.querySelector('.card-preview');
      if (node) {
        const canvas = await html2canvas(node, { backgroundColor: null, scale: 2 });
        cardImage = canvas.toDataURL('image/png');
      }

      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/card/send-card', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : undefined
        },
        body: JSON.stringify({
          cardImage,
          businessInfo,
          clientEmail
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setMessage(data.simulated ? 'Invio simulato: configura SMTP per inviare realmente.' : 'Card inviata con successo!');
        setClientEmail('');
      } else {
        const errorData = await response.json();
        setMessage(`Errore: ${errorData.message}`);
      }
    } catch (error) {
      console.error('Errore durante l\'invio della card:', error);
      setMessage('Si è verificato un errore durante l\'invio della card.');
    }

    setIsLoading(false);
  };
  const handleCreateNewCard = () => {
    navigate('/setup/card-layout');
  };

  if (!cardDesign || !businessInfo) {
    return <div>Caricamento...</div>;
  }

  return (
    <div className="dashboard">
      <h1>Dashboard</h1>
      <div className="card-preview-container" ref={previewContainerRef}>
        <h2>Anteprima Card</h2>
        <CardPreview cardDesign={cardDesign} businessInfo={businessInfo} />
      </div>
      <div className="send-card-section">
        <h2>Invia Card al Cliente</h2>
        <input
          type="email"
          value={clientEmail}
          onChange={handleEmailChange}
          placeholder="Email del cliente"
        />
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button onClick={() => navigate('/setup/card-layout')}>
            Modifica posizioni
          </button>
          <button onClick={handleSendCard} disabled={isLoading || !clientEmail}>
            {isLoading ? 'Invio in corso...' : 'Finalizza e invia'}
          </button>
        </div>
        {message && <p className="message">{message}</p>}
      </div>
      <div className="actions">
        <button onClick={handleCreateNewCard}>Crea Nuova Card</button>
      </div>
    </div>
  );
};

export default Dashboard;